// src/pages/admin/GerenciamentoSessoesPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SessaoForm from '../../components/admin/SessaoForm';
import SessaoCheckinsModal from '../../components/admin/SessaoCheckinsModal';
import GerenciarLocaisSessaoModal from '../../components/admin/GerenciarLocaisSessaoModal'; // <-- NOVO IMPORT
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { getEventoById, getSessoesByEvento, deleteSessao } from '../../services/api';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { PlusCircle, Edit, Trash2, ArrowLeft, ListChecks, MapPin } from 'lucide-react'; // <-- NOVO ÍCONE MapPin
import { Skeleton } from '../../components/ui/Skeleton';
import Breadcrumb from '../../components/Breadcrumb';

function GerenciamentoSessoesPage() {
    const { eventoId } = useParams();
    const navigate = useNavigate();
    const { participanteRole, logout } = useAuth();

    const [evento, setEvento] = useState(null);
    const [sessoes, setSessoes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [sessaoSelecionada, setSessaoSelecionada] = useState(null);

    // Estados para o modal de check-ins
    const [showCheckinsModal, setShowCheckinsModal] = useState(false);
    const [selectedSessaoForCheckins, setSelectedSessaoForCheckins] = useState(null);

    // Novos estados para o modal de gerenciamento de locais de sessão
    const [showLocaisSessaoModal, setShowLocaisSessaoModal] = useState(false);
    const [selectedSessaoForLocais, setSelectedSessaoForLocais] = useState(null);

    const fetchEventoDetails = useCallback(async () => {
        try {
            const data = await getEventoById(eventoId);
            setEvento(data);
        } catch (err) {
            console.error("Erro ao buscar detalhes do evento:", err);
            setError(err.message);
            toast.error(`Erro ao carregar detalhes do evento: ${err.message}`);
        }
    }, [eventoId]);

    const fetchSessoes = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getSessoesByEvento(eventoId);
            setSessoes(data);
        } catch (err) {
            console.error("Erro ao buscar sessões:", err);
            setError(err.message || "Não foi possível carregar as sessões.");
            if (err.message.includes('401') || err.message.includes('403')) {
                logout();
                navigate('/login');
            }
        } finally {
            setLoading(false);
        }
    }, [eventoId, logout, navigate]);

    useEffect(() => {
        if (participanteRole !== 'ROLE_ADMIN') {
            setError("Acesso negado. Apenas administradores podem gerenciar sessões.");
            setLoading(false);
            logout();
            navigate('/login');
            return;
        }
        if (eventoId) {
            fetchEventoDetails();
            fetchSessoes();
        }
    }, [eventoId, participanteRole, logout, navigate, fetchEventoDetails, fetchSessoes]);

    const handleEdit = (sessao) => {
        setSessaoSelecionada(sessao);
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Tem certeza?',
            text: "Você não poderá reverter esta exclusão de sessão! Isso pode afetar dados de presença.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Sim, deletar!',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await deleteSessao(id);
                await fetchSessoes();
                toast.success("Sessão deletada com sucesso!");
            } catch (err) {
                console.error("Erro ao deletar sessão:", err);
                toast.error(`Erro ao deletar sessão: ${err.message}`);
            }
        }
    };

    const handleFormSubmit = () => {
        setShowForm(false);
        setSessaoSelecionada(null);
        fetchSessoes();
    };

    // Funções para o modal de check-ins
    const handleViewCheckins = (sessao) => {
        setSelectedSessaoForCheckins(sessao);
        setShowCheckinsModal(true);
    };

    const handleCloseCheckinsModal = () => {
        setShowCheckinsModal(false);
        setSelectedSessaoForCheckins(null);
    };

    // Funções para o modal de gerenciamento de locais de sessão
    const handleManageLocaisSessao = (sessao) => {
        setSelectedSessaoForLocais(sessao);
        setShowLocaisSessaoModal(true);
    };

    const handleCloseLocaisSessaoModal = () => {
        setShowLocaisSessaoModal(false);
        setSelectedSessaoForLocais(null);
    };

    const SessoesTableSkeleton = () => (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="text-left">
                    <tr className="border-b">
                        <th className="p-4 font-medium">Nome</th>
                        <th className="p-4 font-medium hidden md:table-cell">Início</th>
                        <th className="p-4 font-medium hidden md:table-cell">Fim</th>
                        <th className="p-4 font-medium text-right">Ações</th>
                    </tr>
                </thead>
                <tbody>
                    {Array.from({ length: 4 }).map((_, index) => (
                        <tr key={index} className="border-b">
                            <td className="p-4">
                                <Skeleton className="h-5 w-3/4" />
                                <Skeleton className="h-4 w-1/2 mt-2" />
                            </td>
                            <td className="p-4 hidden md:table-cell"><Skeleton className="h-5 w-3/4" /></td>
                            <td className="p-4 hidden md:table-cell"><Skeleton className="h-5 w-3/4" /></td>
                            <td className="p-4 text-right space-x-2 whitespace-nowrap">
                                <Skeleton className="h-8 w-8 inline-block" />
                                <Skeleton className="h-8 w-8 inline-block" />
                                <Skeleton className="h-8 w-8 inline-block" />
                                <Skeleton className="h-8 w-8 inline-block" /> {/* Para o novo botão */}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    if (error) {
        return <div className="container mx-auto p-4 md:p-6 lg:p-8 text-destructive">Erro: {error}</div>;
    }

    const formatDateTime = (dateTimeString) => {
        if (!dateTimeString) return 'N/A';
        const date = new Date(dateTimeString);
        return date.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
    };

    const breadcrumbItems = [
        { label: 'Gerenciar Eventos', to: '/admin/eventos' },
        { label: evento ? evento.nome : 'Carregando...' }
    ];

    return (
        <div className="container mx-auto p-4 md:p-6 lg:p-8">
            <Breadcrumb items={breadcrumbItems} />
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4"> {/* Adicionado flex-col sm:flex-row e gap-4 */}
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Gerenciamento de Sessões</h1>
                    {evento ? <p className="text-muted-foreground mb-4">Para o evento: {evento.nome}</p> : <Skeleton className="h-5 w-48 mt-1" />} {/* Adicionado mb-4 */}
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto"> {/* Ajustado para responsividade */}
                    <Button variant="outline" onClick={() => navigate('/admin/eventos')} className="w-full sm:w-auto"> {/* Adicionado w-full sm:w-auto */}
                        <ArrowLeft className="mr-2 h-4 w-4" /> Voltar
                    </Button>
                    <Button onClick={() => { setSessaoSelecionada(null); setShowForm(true); }} className="w-full sm:w-auto"> {/* Adicionado w-full sm:w-auto */}
                        <PlusCircle className="mr-2 h-4 w-4" /> Criar Nova Sessão
                    </Button>
                </div>
            </div>

            {showForm && (
                <Card className="mb-6">
                    <CardHeader>
                        <CardTitle className="mb-2">{sessaoSelecionada ? 'Editar Sessão' : 'Criar Nova Sessão'}</CardTitle> {/* Adicionado mb-2 */}
                    </CardHeader>
                    <CardContent>
                        <SessaoForm
                            sessao={sessaoSelecionada}
                            eventoId={eventoId}
                            onSuccess={handleFormSubmit}
                            onCancel={() => { setShowForm(false); setSessaoSelecionada(null); }}
                        />
                    </CardContent>
                </Card>
            )}

            <Card>
                <CardHeader>
                    <CardTitle className="mb-2">Lista de Sessões</CardTitle> {/* Adicionado mb-2 */}
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <SessoesTableSkeleton />
                    ) : sessoes.length === 0 ? (
                        <div className="text-center py-12 border-2 border-dashed rounded-lg">
                            <h3 className="text-lg font-medium text-muted-foreground">Nenhuma sessão cadastrada</h3>
                            <p className="text-sm text-muted-foreground mt-1 mb-4">Adicione sessões a este evento para gerir a presença.</p>
                            <Button onClick={() => { setSessaoSelecionada(null); setShowForm(true); }}>
                                <PlusCircle className="mr-2 h-4 w-4" /> Criar Nova Sessão
                            </Button>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-left">
                                    <tr className="border-b">
                                        <th className="p-4 font-medium">Nome</th>
                                        <th className="p-4 font-medium hidden md:table-cell">Início</th>
                                        <th className="p-4 font-medium hidden md:table-cell">Fim</th>
                                        <th className="p-4 font-medium text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sessoes.map(sessao => (
                                        <tr key={sessao.id} className="border-b hover:bg-accent/50 even:bg-muted/50 transition-colors">
                                            <td className="p-4">
                                                <div className="font-medium">{sessao.nome}</div>
                                                <div className="text-muted-foreground text-xs leading-normal">{sessao.descricao}</div> {/* Adicionado leading-normal */}
                                            </td>
                                            <td className="p-4 text-muted-foreground hidden md:table-cell">{formatDateTime(sessao.dataHoraInicio)}</td>
                                            <td className="p-4 text-muted-foreground hidden md:table-cell">{formatDateTime(sessao.dataHoraFim)}</td>
                                            <td className="p-4 text-right space-x-2 whitespace-nowrap">
                                                <Button variant="outline" size="sm" onClick={() => handleManageLocaisSessao(sessao)}><MapPin className="h-4 w-4" /></Button> {/* NOVO BOTÃO */}
                                                <Button variant="outline" size="sm" onClick={() => handleViewCheckins(sessao)}><ListChecks className="h-4 w-4" /></Button>
                                                <Button variant="outline" size="sm" onClick={() => handleEdit(sessao)}><Edit className="h-4 w-4" /></Button>
                                                <Button variant="destructive" size="sm" onClick={() => handleDelete(sessao.id)}><Trash2 className="h-4 w-4" /></Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {selectedSessaoForCheckins && (
                <SessaoCheckinsModal
                    isOpen={showCheckinsModal}
                    onClose={handleCloseCheckinsModal}
                    sessaoId={selectedSessaoForCheckins.id}
                    sessaoNome={selectedSessaoForCheckins.nome}
                />
            )}

            {selectedSessaoForLocais && (
                <GerenciarLocaisSessaoModal
                    isOpen={showLocaisSessaoModal}
                    onClose={handleCloseLocaisSessaoModal}
                    sessaoId={selectedSessaoForLocais.id}
                    sessaoNome={selectedSessaoForLocais.nome}
                />
            )}
        </div>
    );
}

export default GerenciamentoSessoesPage;